import { AUDIENCE_USER_COUNTS, DEFAULT_FILTER_IDS, MOCK_FILTER_OPTIONS } from './mockFixtures';
import {
  PerformanceAudienceChartMetric,
  PerformanceAudienceDashboardArgs,
  PerformanceAudienceDashboardResponse,
  PerformanceAudienceDateRange,
  PerformanceAudienceFilterOption,
  PerformanceAudienceTrendSeries,
} from './types';

export { DEFAULT_FILTER_IDS };

const DATE_RANGE_MULTIPLIERS: Record<PerformanceAudienceDateRange, number> = {
  last_7_days: 1,
  last_14_days: 1.8,
  last_30_days: 3.2,
};

const BASE_TREND: PerformanceAudienceTrendSeries = {
  impressions: [
    { date: '2026-05-06', label: 'Lun 6', google: 28500, meta: 52000 },
    { date: '2026-05-07', label: 'Mar 7', google: 31200, meta: 54800 },
    { date: '2026-05-08', label: 'Mer 8', google: 29800, meta: 56100 },
    { date: '2026-05-09', label: 'Gio 9', google: 33400, meta: 58900 },
    { date: '2026-05-10', label: 'Ven 10', google: 35100, meta: 61200 },
    { date: '2026-05-11', label: 'Sab 11', google: 27600, meta: 49800 },
    { date: '2026-05-12', label: 'Dom 12', google: 24850, meta: 47320 },
  ],
  clicks: [
    { date: '2026-05-06', label: 'Lun 6', google: 510, meta: 990 },
    { date: '2026-05-07', label: 'Mar 7', google: 558, meta: 1042 },
    { date: '2026-05-08', label: 'Mer 8', google: 534, meta: 1068 },
    { date: '2026-05-09', label: 'Gio 9', google: 598, meta: 1121 },
    { date: '2026-05-10', label: 'Ven 10', google: 629, meta: 1165 },
    { date: '2026-05-11', label: 'Sab 11', google: 495, meta: 948 },
    { date: '2026-05-12', label: 'Dom 12', google: 446, meta: 901 },
  ],
  cost: [
    { date: '2026-05-06', label: 'Lun 6', google: 482, meta: 905 },
    { date: '2026-05-07', label: 'Mar 7', google: 529, meta: 953 },
    { date: '2026-05-08', label: 'Mer 8', google: 506, meta: 978 },
    { date: '2026-05-09', label: 'Gio 9', google: 567, meta: 1027 },
    { date: '2026-05-10', label: 'Ven 10', google: 596, meta: 1068 },
    { date: '2026-05-11', label: 'Sab 11', google: 469, meta: 869 },
    { date: '2026-05-12', label: 'Dom 12', google: 422, meta: 826 },
  ],
  ctr: [
    { date: '2026-05-06', label: 'Lun 6', google: 1.79, meta: 1.9 },
    { date: '2026-05-07', label: 'Mar 7', google: 1.79, meta: 1.9 },
    { date: '2026-05-08', label: 'Mer 8', google: 1.79, meta: 1.9 },
    { date: '2026-05-09', label: 'Gio 9', google: 1.79, meta: 1.9 },
    { date: '2026-05-10', label: 'Ven 10', google: 1.79, meta: 1.9 },
    { date: '2026-05-11', label: 'Sab 11', google: 1.79, meta: 1.9 },
    { date: '2026-05-12', label: 'Dom 12', google: 1.79, meta: 1.9 },
  ],
};

const parseAccountCountFromSuffix = (suffix?: string): number | undefined => {
  if (!suffix) {
    return undefined;
  }

  const match = suffix.match(/(\d+)\s+(?:ad\s+)?account/i);
  return match ? Number(match[1]) : undefined;
};

const findFilterOption = (items: PerformanceAudienceFilterOption[], id?: string) => items.find((item) => item.id === id);

const scaleTrendSeries = (series: PerformanceAudienceTrendSeries, multiplier: number): PerformanceAudienceTrendSeries => {
  const metrics = Object.keys(series) as PerformanceAudienceChartMetric[];

  return metrics.reduce((acc, metric) => {
    acc[metric] = series[metric].map((point) => ({
      ...point,
      google: metric === 'ctr' ? point.google : Math.round(point.google * multiplier),
      meta: metric === 'ctr' ? point.meta : Math.round(point.meta * multiplier),
    }));
    return acc;
  }, {} as PerformanceAudienceTrendSeries);
};

const formatCompactNumber = (value: number) => new Intl.NumberFormat('it-IT', { maximumFractionDigits: 0 }).format(Math.round(value));

const formatEuro = (value: number) => `€ ${new Intl.NumberFormat('it-IT', { maximumFractionDigits: 0 }).format(Math.round(value))}`;

const buildKpis = (multiplier: number) => {
  const impressionsGoogle = 230_450 * multiplier;
  const impressionsMeta = 470_120 * multiplier;
  const costGoogle = 3_378 * multiplier;
  const costMeta = 6_347 * multiplier;

  return [
    { id: 'clusterSize', value: formatCompactNumber(12_450 * (multiplier > 1 ? 1.05 : 1)) },
    { id: 'totalReach', value: formatCompactNumber(145_300 * multiplier), badge: 'meta_only' as const },
    {
      id: 'totalImpressions',
      value: formatCompactNumber(impressionsGoogle + impressionsMeta),
      breakdown: {
        googleLabel: 'Google',
        googleValue: formatCompactNumber(impressionsGoogle),
        metaLabel: 'Meta',
        metaValue: formatCompactNumber(impressionsMeta),
      },
    },
    {
      id: 'totalCost',
      value: formatEuro(costGoogle + costMeta),
      breakdown: {
        googleLabel: 'Google',
        googleValue: formatEuro(costGoogle),
        metaLabel: 'Meta',
        metaValue: formatEuro(costMeta),
      },
    },
  ];
};

export const buildMockPerformanceAudienceDashboard = (args: PerformanceAudienceDashboardArgs): PerformanceAudienceDashboardResponse => {
  const dateRange = (args.dateRange ?? DEFAULT_FILTER_IDS.dateRange) as PerformanceAudienceDateRange;
  const multiplier = DATE_RANGE_MULTIPLIERS[dateRange] ?? 1;

  const selectedAudience = findFilterOption(MOCK_FILTER_OPTIONS.audiences, args.audienceId);
  const selectedGoogleScope = findFilterOption(MOCK_FILTER_OPTIONS.googleScopes, args.googleScopeId);
  const selectedMetaScope = findFilterOption(MOCK_FILTER_OPTIONS.metaScopes, args.metaScopeId);

  const googleAccountCount = parseAccountCountFromSuffix(selectedGoogleScope?.suffix) ?? 3;
  const metaAdAccountCount = parseAccountCountFromSuffix(selectedMetaScope?.suffix) ?? 5;
  const audienceUserCount = args.audienceId ? AUDIENCE_USER_COUNTS[args.audienceId] ?? 12_450 : 12_450;

  return {
    summary: {
      audienceName: selectedAudience?.value ?? '',
      audienceUserCount,
      googleAccountCount,
      metaAdAccountCount,
      googleCampaignCount: Math.max(1, Math.round(2 * (googleAccountCount / 3))),
      metaAdSetCount: Math.max(1, Math.round(3 * (metaAdAccountCount / 5))),
    },
    filterOptions: MOCK_FILTER_OPTIONS,
    kpis: buildKpis(multiplier),
    trend: scaleTrendSeries(BASE_TREND, multiplier),
    comparison: [
      { id: 'impressions', google: { value: formatCompactNumber(230_450 * multiplier) }, meta: { value: formatCompactNumber(470_120 * multiplier) } },
      { id: 'reach', google: { value: '', unavailable: true }, meta: { value: formatCompactNumber(145_300 * multiplier) } },
      { id: 'frequency', google: { value: '2,4' }, meta: { value: '3,2' } },
      { id: 'clicks', google: { value: formatCompactNumber(4_120 * multiplier) }, meta: { value: formatCompactNumber(8_934 * multiplier) } },
      { id: 'ctr', google: { value: '1,79%' }, meta: { value: '1,90%' } },
      { id: 'cpc', google: { value: formatEuro(0.82) }, meta: { value: formatEuro(0.71) } },
      { id: 'totalCost', google: { value: formatEuro(3_378 * multiplier) }, meta: { value: formatEuro(6_347 * multiplier) } },
      { id: 'storeVisits', google: { value: formatCompactNumber(240 * multiplier) }, meta: { value: '', unavailable: true } },
    ],
    storeVisits: {
      total: Math.round(240 * multiplier),
      locationCount: 3,
      status: 'active',
      locations: [
        { id: 'milano', name: 'Milano Centro', visits: Math.round(124 * multiplier), share: 52 },
        { id: 'roma', name: 'Roma EUR', visits: Math.round(78 * multiplier), share: 33 },
        { id: 'torino', name: 'Torino S.S.', visits: Math.round(38 * multiplier), share: 15 },
      ],
    },
  };
};

/** @deprecated Use DEFAULT_FILTER_IDS from mockFixtures instead */
export const DEFAULT_MOCK_FILTER_IDS = DEFAULT_FILTER_IDS;
